import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, api } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Phone, ArrowRight, ArrowLeft, UserPlus, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const FONCTIONS = [
  { value: 'teleprospectrice', label: 'Téléprospectrice' },
  { value: 'commercial', label: 'Commercial(e)' },
  { value: 'assistante', label: 'Assistante' },
  { value: 'admin', label: 'Admin' },
  { value: 'direction', label: 'Direction générale' },
];

export default function LoginPage() {
  const [mode, setMode] = useState('login'); // login | register | forgot | reset
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [fonction, setFonction] = useState('teleprospectrice');
  const [confirmPw, setConfirmPw] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Where to redirect after login : prefer the page the user came from
  const from = (() => {
    const f = location.state?.from;
    if (f) return (f.pathname || '/') + (f.search || '');
    return '/';
  })();

  // Check URL for reset token
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      setResetToken(token);
      setMode('reset');
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Erreur de connexion');
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (password !== confirmPw) { setError('Les mots de passe ne correspondent pas'); return; }
    if (password.length < 6) { setError('Le mot de passe doit faire au moins 6 caractères'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/register', { prenom, nom, email, password, fonction });
      setSuccess(data.message);
      setMode('login');
      toast.success('Inscription envoyée !');
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : "Erreur d'inscription");
    }
    setLoading(false);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email });
      setSuccess(data.message);
      toast.success('Email de réinitialisation envoyé');
    } catch {
      setError('Erreur');
    }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    if (newPassword.length < 6) { setError('Le mot de passe doit faire au moins 6 caractères'); setLoading(false); return; }
    try {
      const { data } = await api.post('/auth/reset-password', { token: resetToken, password: newPassword });
      setSuccess(data.message);
      setMode('login');
      toast.success('Mot de passe réinitialisé !');
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Erreur');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left: Visual */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #F97316 0%, #1A1A1A 100%)' }}>
        <div className="text-center text-white px-12 animate-fade-in">
          <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-8">
            <Phone className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            <span className="text-[#F97316]">WASABI</span> ProspecTel
          </h1>
          <p className="text-lg text-white/80 max-w-md mx-auto">
            Prospection commerciale automatisée. Importez, enrichissez, convertissez.
          </p>
        </div>
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-[#F97316]/10 rounded-full" />
        <div className="absolute -top-10 -left-10 w-60 h-60 bg-[#F97316]/10 rounded-full" />
      </div>

      {/* Right: Forms */}
      <div className="flex-1 flex items-center justify-center px-6 lg:px-16 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          <div className="lg:hidden flex items-center gap-2.5 mb-10">
            <div className="w-10 h-10 rounded-xl bg-[#F97316] flex items-center justify-center">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}><span className="text-[#F97316]">WASABI</span> ProspecTel</span>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="auth-error">{error}</div>
          )}
          {success && (
            <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700" data-testid="auth-success">{success}</div>
          )}

          {/* === LOGIN === */}
          {mode === 'login' && (
            <>
              <h2 className="text-2xl font-bold tracking-tight mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Connexion</h2>
              <p className="text-sm text-[#71717A] mb-6">Entrez vos identifiants pour accéder à votre espace</p>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Email</Label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="votre@email.com" required
                    data-testid="login-email-input" className="h-11 bg-white border-[#E5E7EB]" />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Mot de passe</Label>
                  <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Votre mot de passe" required
                    data-testid="login-password-input" className="h-11 bg-white border-[#E5E7EB]" />
                </div>
                <Button type="submit" disabled={loading} data-testid="login-submit-btn"
                  className="w-full h-11 bg-[#F97316] hover:bg-[#EA580C] text-white font-medium transition-all active:scale-[0.98]">
                  {loading ? 'Connexion...' : <><span>Se connecter</span><ArrowRight className="w-4 h-4 ml-2" /></>}
                </Button>
              </form>
              <div className="mt-5 flex flex-col items-center gap-3">
                <button onClick={() => { setError(''); setSuccess(''); setMode('forgot'); }}
                  className="text-sm text-[#71717A] hover:text-[#F97316] transition-colors" data-testid="forgot-password-link">
                  <KeyRound className="w-3.5 h-3.5 inline mr-1" />Mot de passe oublié ?
                </button>
                <div className="w-full border-t border-[#E5E7EB] pt-4">
                  <button onClick={() => { setError(''); setSuccess(''); setMode('register'); }}
                    className="w-full h-11 border border-[#E5E7EB] rounded-lg text-sm font-medium hover:bg-[#F9FAFB] transition-colors flex items-center justify-center gap-2"
                    data-testid="go-to-register-btn">
                    <UserPlus className="w-4 h-4" /> Créer un compte
                  </button>
                </div>
              </div>
            </>
          )}

          {/* === REGISTER === */}
          {mode === 'register' && (
            <>
              <button onClick={() => { setError(''); setSuccess(''); setMode('login'); }}
                className="text-sm text-[#71717A] hover:text-[#09090B] mb-4 flex items-center gap-1" data-testid="back-to-login">
                <ArrowLeft className="w-3.5 h-3.5" /> Retour connexion
              </button>
              <h2 className="text-2xl font-bold tracking-tight mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Créer un compte</h2>
              <p className="text-sm text-[#71717A] mb-6">Remplissez le formulaire. Votre demande sera validée par un administrateur.</p>
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium mb-1 block">Prénom</Label>
                    <Input value={prenom} onChange={e => setPrenom(e.target.value)} placeholder="Prénom" required
                      data-testid="register-prenom" className="h-10 bg-white border-[#E5E7EB]" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-1 block">Nom</Label>
                    <Input value={nom} onChange={e => setNom(e.target.value)} placeholder="Nom" required
                      data-testid="register-nom" className="h-10 bg-white border-[#E5E7EB]" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1 block">Email</Label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="votre@email.com" required
                    data-testid="register-email" className="h-10 bg-white border-[#E5E7EB]" />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1 block">Fonction</Label>
                  <Select value={fonction} onValueChange={setFonction}>
                    <SelectTrigger className="h-10 bg-white" data-testid="register-fonction"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FONCTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1 block">Mot de passe</Label>
                  <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 6 caractères" required
                    data-testid="register-password" className="h-10 bg-white border-[#E5E7EB]" />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1 block">Confirmer le mot de passe</Label>
                  <Input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Confirmez votre mot de passe" required
                    data-testid="register-confirm-password" className="h-10 bg-white border-[#E5E7EB]" />
                </div>
                <Button type="submit" disabled={loading} data-testid="register-submit-btn"
                  className="w-full h-11 bg-[#F97316] hover:bg-[#EA580C] text-white font-medium transition-all active:scale-[0.98]">
                  {loading ? 'Inscription...' : <><UserPlus className="w-4 h-4 mr-2" /><span>Envoyer ma demande</span></>}
                </Button>
              </form>
            </>
          )}

          {/* === FORGOT PASSWORD === */}
          {mode === 'forgot' && (
            <>
              <button onClick={() => { setError(''); setSuccess(''); setMode('login'); }}
                className="text-sm text-[#71717A] hover:text-[#09090B] mb-4 flex items-center gap-1" data-testid="back-to-login-forgot">
                <ArrowLeft className="w-3.5 h-3.5" /> Retour connexion
              </button>
              <h2 className="text-2xl font-bold tracking-tight mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Mot de passe oublié</h2>
              <p className="text-sm text-[#71717A] mb-6">Entrez votre email pour recevoir un lien de réinitialisation</p>
              <form onSubmit={handleForgot} className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Email</Label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="votre@email.com" required
                    data-testid="forgot-email" className="h-11 bg-white border-[#E5E7EB]" />
                </div>
                <Button type="submit" disabled={loading} data-testid="forgot-submit-btn"
                  className="w-full h-11 bg-[#F97316] hover:bg-[#EA580C] text-white font-medium">
                  {loading ? 'Envoi...' : 'Envoyer le lien de réinitialisation'}
                </Button>
              </form>
            </>
          )}

          {/* === RESET PASSWORD === */}
          {mode === 'reset' && (
            <>
              <h2 className="text-2xl font-bold tracking-tight mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Nouveau mot de passe</h2>
              <p className="text-sm text-[#71717A] mb-6">Choisissez votre nouveau mot de passe</p>
              <form onSubmit={handleReset} className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Nouveau mot de passe</Label>
                  <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min. 6 caractères" required
                    data-testid="reset-new-password" className="h-11 bg-white border-[#E5E7EB]" />
                </div>
                <Button type="submit" disabled={loading} data-testid="reset-submit-btn"
                  className="w-full h-11 bg-[#F97316] hover:bg-[#EA580C] text-white font-medium">
                  {loading ? 'Réinitialisation...' : 'Réinitialiser mon mot de passe'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
